import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { Apollo } from 'apollo-angular';

import { DataService } from '../data/data.service';
import { StateService } from '../state/state.service';
import {
    GET_WISHLIST,
    ADD_TO_WISHLIST,
    REMOVE_FROM_WISHLIST,
    CLEAR_WISHLIST,
} from '../../../common/graphql/wishlist.graphql';

export interface WishlistItem {
    id: string;
    createdAt: string;
    productVariant: {
        id: string;
        name: string;
        priceWithTax: number;
        product: {
            id: string;
            slug: string;
            name: string;
            featuredAsset?: { id: string; preview: string };
        };
        featuredAsset?: { id: string; preview: string };
    };
    note?: string;
    priority: number;
    availableStock: number;
    isInStock: boolean;
}

@Injectable({ providedIn: 'root' })
export class WishlistService {
    private countSubject = new BehaviorSubject<number>(0);
    private wishlistIdsSubject = new BehaviorSubject<Set<string>>(new Set());
    private initialized = false;
    count$ = this.countSubject.asObservable();
    wishlistIds$ = this.wishlistIdsSubject.asObservable();

    constructor(
        private dataService: DataService,
        private stateService: StateService,
        private apollo: Apollo,
    ) {
        this.stateService.select(state => state.signedIn).subscribe(signedIn => {
            if (signedIn && !this.initialized) {
                this.initialized = true;
                this.loadAllWishlistIds();
            } else if (!signedIn) {
                this.initialized = false;
                this.countSubject.next(0);
                this.wishlistIdsSubject.next(new Set());
            }
        });
    }

    isInWishlistSync(productVariantId: string): boolean {
        return this.wishlistIdsSubject.value.has(productVariantId);
    }

    getWishlist(): Observable<WishlistItem[]> {
        return new Observable<WishlistItem[]>(subscriber => {
            this.apollo.query<{ activeCustomerWishlist: WishlistItem[] }>({
                query: GET_WISHLIST,
                fetchPolicy: 'network-only',
            }).subscribe({
                next: result => {
                    subscriber.next(result.data?.activeCustomerWishlist || []);
                    subscriber.complete();
                },
                error: err => subscriber.error(err),
            });
        });
    }

    addToWishlist(productVariantId: string): Observable<any> {
        const ids = new Set(this.wishlistIdsSubject.value);
        ids.add(productVariantId);
        this.wishlistIdsSubject.next(ids);
        this.countSubject.next(ids.size);

        return this.mutateNoCache(ADD_TO_WISHLIST, { input: { productVariantId } }).pipe(
            tap({
                error: () => {
                    const revert = new Set(this.wishlistIdsSubject.value);
                    revert.delete(productVariantId);
                    this.wishlistIdsSubject.next(revert);
                    this.countSubject.next(revert.size);
                },
            }),
        );
    }

    removeFromWishlist(productVariantId: string): Observable<any> {
        const ids = new Set(this.wishlistIdsSubject.value);
        ids.delete(productVariantId);
        this.wishlistIdsSubject.next(ids);
        this.countSubject.next(ids.size);

        return this.mutateNoCache(REMOVE_FROM_WISHLIST, { productVariantId }).pipe(
            tap({
                error: () => {
                    const revert = new Set(this.wishlistIdsSubject.value);
                    revert.add(productVariantId);
                    this.wishlistIdsSubject.next(revert);
                    this.countSubject.next(revert.size);
                },
            }),
        );
    }

    clearWishlist(): Observable<any> {
        const backup = new Set(this.wishlistIdsSubject.value);
        this.wishlistIdsSubject.next(new Set());
        this.countSubject.next(0);

        return this.mutateNoCache(CLEAR_WISHLIST, {}).pipe(
            tap({
                error: () => {
                    this.wishlistIdsSubject.next(backup);
                    this.countSubject.next(backup.size);
                },
            }),
        );
    }

    toggleWishlist(productVariantId: string): Observable<any> {
        if (this.isInWishlistSync(productVariantId)) {
            return this.removeFromWishlist(productVariantId);
        } else {
            return this.addToWishlist(productVariantId);
        }
    }

    private mutateNoCache(mutation: any, variables: any): Observable<any> {
        return new Observable(subscriber => {
            this.apollo.mutate({
                mutation,
                variables,
                fetchPolicy: 'no-cache',
            }).subscribe({
                next: result => {
                    subscriber.next(result.data);
                    subscriber.complete();
                },
                error: err => subscriber.error(err),
            });
        });
    }

    private loadAllWishlistIds() {
        this.apollo.query<{ activeCustomerWishlist: WishlistItem[] }>({
            query: GET_WISHLIST,
            fetchPolicy: 'network-only',
        }).subscribe({
            next: result => {
                const items = result.data?.activeCustomerWishlist || [];
                const ids = new Set(items.map(item => item.productVariant.id));
                this.wishlistIdsSubject.next(ids);
                this.countSubject.next(ids.size);
            },
            error: () => {
                this.wishlistIdsSubject.next(new Set());
                this.countSubject.next(0);
            },
        });
    }
}
